import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

// Injected at build time by vite.config.js (undefined in dev/tests)
/* global __APP_VERSION__ */
const CURRENT_VERSION =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const RELOAD_GUARD_KEY = 'appReloadedForVersion'

// Server sends no cache headers, so bypass the browser cache explicitly:
// no-store + unique query string guarantees a network fetch.
const fetchDeployedVersion = async () => {
  try {
    const res = await fetch(
      `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`,
      { cache: 'no-store' }
    )
    if (!res.ok) return ''
    const json = await res.json()
    return typeof json?.version === 'string' ? json.version : ''
  } catch {
    return ''
  }
}

// A plain reload() may serve index.html from the browser's heuristic cache
// (no Cache-Control from nginx). Loading a new URL (?v=<version>) forces a
// fresh index.html, which references the new hashed JS/CSS bundles.
// HashRouter keeps the route in the #hash, so the current screen is preserved.
const reloadToVersion = version => {
  // Loop guard: only attempt one forced reload per target version per tab
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === version) return
  sessionStorage.setItem(RELOAD_GUARD_KEY, version)

  const url = new URL(window.location.href)
  url.searchParams.set('v', version)
  window.location.replace(url.toString())
}

/**
 * Detects a newer deployment and loads it without the user hard-reloading.
 * - On app start: if the bundle is stale, reload immediately (nothing to lose yet).
 * - While running: reload on the next route change or when the tab regains
 *   focus, so unsaved form data on the current screen isn't lost.
 */
const AppVersionWatcher = () => {
  const location = useLocation()
  const pendingVersion = useRef('')
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (import.meta.env.DEV || !CURRENT_VERSION) return undefined

    const check = async () => {
      const deployed = await fetchDeployedVersion()
      if (deployed && deployed !== CURRENT_VERSION) {
        pendingVersion.current = deployed
      }
      return pendingVersion.current
    }

    // Startup check — stale cached index.html/bundle is replaced right away
    check().then(version => {
      // Verification log: confirms which build is running in the browser vs
      // which build is deployed on the server (version = build timestamp).
      console.info('[AppVersion]', {
        running: CURRENT_VERSION,
        runningBuiltAt: new Date(Number(CURRENT_VERSION)).toISOString(),
        deployed: version || CURRENT_VERSION,
        status: version ? 'outdated - reloading to latest' : 'up to date',
      })
      if (version) reloadToVersion(version)
    })

    const interval = setInterval(check, CHECK_INTERVAL_MS)

    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return
      const version = await check()
      if (version) reloadToVersion(version)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // A lazily-loaded chunk from the old build no longer exists on the server
    const onPreloadError = async event => {
      event.preventDefault()
      const version = (await check()) || String(Date.now())
      reloadToVersion(version)
    }
    window.addEventListener('vite:preloadError', onPreloadError)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('vite:preloadError', onPreloadError)
    }
  }, [])

  // Apply a pending update on route change (user is leaving the current screen)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (pendingVersion.current) reloadToVersion(pendingVersion.current)
  }, [location.pathname])

  return null
}

export default AppVersionWatcher
